import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Check, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import { SITE } from "@/lib/site";
import { PROJECTS } from "@/data/projects";

/**
 * ServicePageLayout — shared visual skeleton for the commercial landing pages.
 * All content is passed in via props/data; visual identity matches the rest of
 * the site (charcoal + orange, generous whitespace, no sign-shop template look).
 */
export default function ServicePageLayout({
  seoTitle,
  seoDescription,
  path,
  eyebrow,
  h1,
  intro, // array of paragraphs (strings)
  sections, // [{ id?, heading, kind: 'paragraphs'|'list'|'both', paragraphs?, items? }]
  serviceArea, // { heading, description, cities: [] }
  faqs, // [{ q, a }]
  relatedProjectSlugs, // string[]
  bottomCta, // { headline, body }
  breadcrumbLabel = h1,
}) {
  const related = (relatedProjectSlugs || [])
    .map((s) => PROJECTS.find((p) => p.slug === s))
    .filter(Boolean);

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://rvasigns.com/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": breadcrumbLabel,
        "item": `https://rvasigns.com${path}`,
      },
    ],
  };

  const faqJsonLd =
    faqs && faqs.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }
      : null;

  const combinedJsonLd = faqJsonLd ? [breadcrumbJsonLd, faqJsonLd] : breadcrumbJsonLd;

  return (
    <div data-testid={`service-page-${path.replace(/\//g, "")}`} className="bg-white">
      <Seo path={path} title={seoTitle} description={seoDescription} jsonLd={combinedJsonLd} />

      {/* BREADCRUMB */}
      <nav className="max-w-5xl mx-auto px-6 lg:px-8 pt-8 pb-2" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-xs text-[#1A1A1A]/60">
          <li>
            <Link to="/" className="hover:text-[#F97316] transition-colors font-semibold uppercase tracking-wider">
              Home
            </Link>
          </li>
          <li className="text-[#1A1A1A]/30">
            <ChevronRight className="w-3 h-3" />
          </li>
          <li className="text-[#1A1A1A]/80 font-semibold uppercase tracking-wider truncate max-w-[70vw]">
            {breadcrumbLabel}
          </li>
        </ol>
      </nav>

      {/* HERO */}
      <section className="pt-10 pb-14 lg:pt-16 lg:pb-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {eyebrow && (
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-0.5 bg-[#F97316]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">
                {eyebrow}
              </span>
            </div>
          )}
          <h1
            className="font-heading text-[#1A1A1A] tracking-tight leading-[0.98]"
            style={{ fontWeight: 900, fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
            data-testid="service-h1"
          >
            {h1}
          </h1>
          <div className="mt-10 max-w-2xl space-y-5 text-lg text-[#2B2F33]/85 leading-relaxed">
            {intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="service-hero-cta-primary"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors"
            >
              Discuss Your Sign Project <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href={SITE.phoneHref}
              data-testid="service-hero-cta-call"
              className="border-2 border-[#1A1A1A]/15 hover:border-[#1A1A1A] text-[#1A1A1A] text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="pb-16 lg:pb-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 space-y-14 lg:space-y-20">
          {sections.map((sec, i) => (
            <div key={i} id={sec.id}>
              <h2
                className="font-heading text-[#1A1A1A] tracking-tight leading-[1.05]"
                style={{ fontWeight: 900, fontSize: "clamp(1.875rem, 3.5vw, 2.5rem)" }}
              >
                {sec.heading}
              </h2>
              {(sec.kind === "paragraphs" || sec.kind === "both") && sec.paragraphs && (
                <div className="mt-6 space-y-4 text-lg text-[#2B2F33]/85 leading-relaxed max-w-3xl">
                  {sec.paragraphs.map((p, k) => (
                    <p key={k}>{p}</p>
                  ))}
                </div>
              )}
              {(sec.kind === "list" || sec.kind === "both") && sec.items && (
                <ul className="mt-6 space-y-3 max-w-3xl">
                  {sec.items.map((item, k) => (
                    <li key={k} className="flex items-start gap-3 text-lg text-[#2B2F33]/90">
                      <Check className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {serviceArea && (
            <div>
              <h2
                className="font-heading text-[#1A1A1A] tracking-tight leading-[1.05]"
                style={{ fontWeight: 900, fontSize: "clamp(1.875rem, 3.5vw, 2.5rem)" }}
              >
                {serviceArea.heading}
              </h2>
              <p className="mt-6 text-lg text-[#2B2F33]/85 leading-relaxed max-w-3xl">
                {serviceArea.description}
              </p>
              <div className="mt-6 flex flex-wrap gap-2 max-w-3xl">
                {serviceArea.cities.map((c) => (
                  <span
                    key={c}
                    className="text-sm font-semibold px-3 py-1.5 bg-[#F8F8F5] border border-[#1A1A1A]/10 text-[#1A1A1A]"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}

          {faqs && faqs.length > 0 && (
            <div>
              <h2
                className="font-heading text-[#1A1A1A] tracking-tight leading-[1.05]"
                style={{ fontWeight: 900, fontSize: "clamp(1.875rem, 3.5vw, 2.5rem)" }}
              >
                Frequently asked questions
              </h2>
              <div className="mt-8 divide-y divide-[#1A1A1A]/10 max-w-3xl">
                {faqs.map((f, k) => (
                  <details key={k} className="group py-5">
                    <summary className="cursor-pointer flex items-start justify-between gap-6 font-heading text-lg lg:text-xl text-[#1A1A1A] leading-snug list-none" style={{ fontWeight: 700 }}>
                      <span>{f.q}</span>
                      <ChevronRight className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-1 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-base text-[#2B2F33]/85 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* RELATED PROJECTS */}
      {related.length > 0 && (
        <section className="py-16 lg:py-24 bg-[#F8F8F5] border-t border-[#1A1A1A]/8">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-0.5 bg-[#F97316]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">
                Relevant Project Examples
              </span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1A1A1A] leading-[1.05]" style={{ fontWeight: 900 }}>
              A few relevant projects.
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/projects/${p.slug}`}
                  data-testid={`related-project-${p.slug}`}
                  className="group block"
                >
                  <div className="aspect-[4/3] bg-white border border-[#1A1A1A]/8 overflow-hidden">
                    {p.coverImage ? (
                      <img
                        src={p.coverImage}
                        alt={p.title}
                        className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#F8F8F5]" />
                    )}
                  </div>
                  <h3 className="mt-4 font-heading text-lg text-[#1A1A1A] tracking-tight leading-tight" style={{ fontWeight: 800 }}>
                    {p.title}
                  </h3>
                </Link>
              ))}
            </div>
            <div className="mt-10">
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#F97316] hover:gap-3 transition-all"
              >
                See all project examples <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* BOTTOM CTA */}
      <section className="py-20 lg:py-28 bg-white border-t border-[#1A1A1A]/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05]" style={{ fontWeight: 900 }}>
            {bottomCta.headline}
          </h2>
          <p className="mt-6 text-lg text-[#2B2F33]/80 leading-relaxed max-w-xl mx-auto">
            {bottomCta.body}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              data-testid="service-bottom-cta-primary"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors"
            >
              Discuss Your Sign Project <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href={SITE.phoneHref}
              data-testid="service-bottom-cta-call"
              className="border-2 border-[#1A1A1A]/15 hover:border-[#1A1A1A] text-[#1A1A1A] text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
